import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  AppBar,
  Toolbar,
} from "@mui/material";

//... Icon imports
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";

//... Context & Services
import AppDataContext from "../../../common/appContext";
import { Game5On5Services } from "../../../services/game5On5.Service";

//... Shared Components
import TitleTooltip from "../../../sharedComponents/Tooltip/TitleTooltip";
import EmptyUI from "../../../sharedComponents/emptyStateUIContainers/EmptyUI";
import { Video_baseUrl } from "../../../services";
import Helper from "../../../common/Helper";

export default function Player5On5Games({ playerID, setLoading }) {
  //... Context
  const { selectedEventId } = useContext(AppDataContext);

  //... Local States
  const [games, setGames] = useState([]);
  const [active, setActive] = useState(null);

  //... Effects
  useEffect(() => {
    if (!selectedEventId || !playerID) return;
    fetchGames();
  }, [selectedEventId, playerID]);

  //... Handlers
  const openNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePlay = (video) => setActive(video);

  //... API Calls
  const fetchGames = async () => {
    setLoading(true);
    try {
      const res = await Game5On5Services.get5On5GameVideos(
        selectedEventId,
        playerID
      );
      setGames(res?.data?.data || []);
    } catch {
      setGames([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: 3, py: 5 }}>
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Typography
          sx={{
            fontWeight: 900,
            mb: 5,
            fontSize: { xs: 24, md: 34 },
            color: "#111827",
            letterSpacing: 0.5,
          }}
        >
          🎥 Game Videos
        </Typography>
      </Box>

      {games?.length === 0 && (
        <Grid container p={1}>
          <Grid item lg={12}>
            <EmptyUI
              heading="No Game Videos Found."
              subHeading="Try selecting another event or player."
              icon={<InfoIcon />}
              styles={{
                minHeight: "30vh",
                background: "linear-gradient(135deg, #f9fafb, #eef2ff)",
                borderRadius: "16px",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
              iconStyles={{
                fontSize: 96,
                color: "#99979f",
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                borderRadius: "50%",
                backgroundColor: "#f5f5f5",
                display: "inline-block",
              }}
            />
          </Grid>
        </Grid>
      )}

      {games.length > 0 && (
        <Grid container spacing={3}>
          {games.map((video) => {
            const { gameVideosId, gameName, url } = video;

            return (
              <Grid item xs={12} sm={6} md={3} key={gameVideosId}>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                    transition: "0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 18px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#111827",
                        }}
                      >
                        {gameName}
                      </Typography>
                      {Helper.isHttpVideoURL(url) && (
                        <TitleTooltip
                          title="Open video in new tab"
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={() => openNewTab(url)}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </TitleTooltip>
                      )}
                    </Box>

                    <Box
                      onClick={() => handlePlay(video)}
                      sx={{
                        height: 160,
                        borderRadius: 2,
                        background: "#eef2f7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <PlayCircleOutlineIcon
                        sx={{
                          fontSize: 48,
                          color: "#3b82f6",
                        }}
                      />
                    </Box>

                    <Button
                      fullWidth
                      onClick={() => handlePlay(video)}
                      sx={{
                        mt: 2,
                        borderRadius: 2,
                        textTransform: "none",
                        background: "#3b82f6",
                        color: "#fff",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#2563eb",
                        },
                      }}
                    >
                      Play
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={Boolean(active)}
        onClose={() => setActive(null)}
        fullWidth
        maxWidth="md"
      >
        {active && (
          <Box sx={{ background: "#000" }}>
            <AppBar position="static" sx={{ background: "#111827" }}>
              <Toolbar
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {active.gameName}
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  {Helper.isHttpVideoURL(active.url) && (
                    <TitleTooltip title="Open in new tab">
                      <IconButton
                        onClick={() => openNewTab(active.url)}
                        sx={{ color: "#fff" }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </TitleTooltip>
                  )}

                  <IconButton
                    onClick={() => setActive(null)}
                    sx={{ color: "#fff" }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Toolbar>
            </AppBar>

            {!Helper.isHttpVideoURL(active.url) ? (
              <video
                src={Video_baseUrl(active.url)}
                controls
                autoPlay
                style={{
                  width: "100%",
                  height: "70vh",
                  backgroundColor: "#000",
                }}
              />
            ) : (
              <iframe
                src={active.url}
                title={active.gameName}
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "none",
                }}
                loading="lazy"
                allowFullScreen
              />
            )}
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
